import React from "react";
import Photo1 from "../images/cat.jpg";
import Photo2 from "../images/planter.jpg";
import Vetchling from "../images/vetchling.png";
import Fox1 from "../images/wildlife/fox1.jpg";
import Fox2 from "../images/wildlife/fox2.jpg";
import Fox3 from "../images/wildlife/fox3.jpg";
import Fox4 from "../images/wildlife/fox4.jpg";
import Fox5 from "../images/wildlife/fox5.jpg";
import Fox6 from "../images/wildlife/fox6.jpg";
import Fox7 from "../images/wildlife/fox7.jpg";
import Toad from "../images/wildlife/toad.jpg";
import Slowworm from "../images/wildlife/slowworm.jpg";
import BeePhoto from "../images/bees.jpg";

function Conservation() {
  return (
    <div className="conservation container">
      {/* Hero */}
      <section className="hero-section text-center mb-5">
        <h1>Conservation Efforts</h1>
      </section>

      {/* Water Management */}
      <section className="mb-5">
        <h2>Using Water Responsibly</h2>
        <p>
          As the Society operates on a very small budget, careful water use is
          essential. Protecting local habitats also depends on how we manage
          shared resources.
        </p>

        <ul className="list-group list-group-flush mb-4">
          <li className="list-group-item d-flex justify-content-between align-items-center">
            🚫 Hosepipes are not permitted
            <span className="badge bg-danger rounded-pill">Strict rule</span>
          </li>
          <li className="list-group-item">
            💧 <strong>Check taps</strong> – ensure all taps are fully turned
            off.
          </li>
          <li className="list-group-item">
            🌧️ <strong>Use rainwater first</strong>
            <ul className="small text-muted mt-2">
              <li>Rainwater tanks are located behind the shed.</li>
              <li>
                The far-end tank is mains-fed and should be a last resort.
              </li>
            </ul>
          </li>
          <li className="list-group-item">
            🔧 <strong>Report leaks</strong> promptly.
          </li>
          <li className="list-group-item">
            🌱 <strong>Water only when needed</strong>, especially during dry
            periods.
          </li>
        </ul>
      </section>

      {/* Locations */}
      <section className="mb-5">
        <h2>Local Green Spaces & Wildlife Corridors</h2>
        <p>
          Continuous green spaces allow wildlife to move freely, access
          resources, and sustain healthy populations. In urban Lewisham, these
          corridors are vital for biodiversity.
        </p>
        <p>
          Routes such as the Green Chain Walk and Waterlink Way help connect
          habitats. Allotments also play an important role for pollinators,
          birds, and small mammals.
        </p>

        <div className="location-list">
          {[
            [
              "Chinbrook Meadows Park",
              "Grasslands, mature trees, rivers and hedgerows.",
            ],
            ["Elmstead Woods", "A large area of ancient woodland."],
            ["Grove Park Cemetery", "Grassland flowers and mature trees."],
            ["Sunridge Park Golf Course", "A quiet green space for walking."],
            [
              "Adjacent Wooded Railway Cutting",
              "An important wildlife corridor.",
            ],
            [
              "Household Gardens",
              "Private gardens planted with native species.",
            ],
          ].map(([title, desc]) => (
            <div className="location-card" key={title}>
              <h5 className="location-title">{title}</h5>
              <p className="location-description">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Biodiversity */}
      <section className="mb-5">
        <h2>Wildlife & Plant Life</h2>
        <p>
          These connected habitats support a wide range of flora and fauna.
          Protecting them helps maintain ecological balance across the area.
        </p>

        <div className="card-deck">
          {[
            [
              "Birds",
              "Blackcaps, Blue Tits, Buzzards, Carrion Crows and Coal Tits.",
            ],
            [
              "Mammals",
              "Foxes, Field Mice, Wood Mice, Hedgehogs and Brown Rats.",
            ],
            [
              "Reptiles & Amphibians",
              "Common Lizards, Slow Worms, Frogs and Toads.",
            ],
            [
              "Invertebrates",
              "Gatekeeper, Brimstone, Meadow Brown and Red Admiral butterflies.",
            ],
            [
              "Rare / Local Plants",
              "Grass Vetchling, Wild Rose, Meadow Vetchling and Hairy Tare.",
            ],
          ].map(([title, text]) => (
            <div className="card wildlife-card" key={title}>
              <div className="card-body">
                <h5 className="card-title">{title}</h5>
                <p className="card-text">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Key Species Cards */}
      <section className="mb-5">
        <h2>Key Species and Their Roles</h2>

        <div className="card-deck">
          <div className="card wildlife-card">
            <div className="card-body">
              <h5 className="card-title">Slugs</h5>
              <p className="card-text">
                Many slug species help break down organic matter and return
                nutrients to the soil. Harmful species can be managed through
                crop rotation, barriers, and natural predators. Learn more from{" "}
                <a
                  href="https://www.charlesdowding.co.uk/resources/slug-reduction"
                  target="_blank"
                  rel="noreferrer"
                >
                  Charles Dowding
                </a>{" "}
                and the{" "}
                <a
                  href="https://www.rhs.org.uk/biodiversity/slugs-and-snails"
                  target="_blank"
                  rel="noreferrer"
                >
                  RHS
                </a>
                .
              </p>
            </div>
          </div>

          <div className="card wildlife-card">
            <div className="card-body">
              <h5 className="card-title">Wasps</h5>
              <p className="card-text">
                Wasps help control pest populations and also contribute to
                pollination. A lower population this year highlights the
                importance of protecting their habitat. More information is
                available from the{" "}
                <a
                  href="https://www.nhm.ac.uk/discover/what-do-wasps-do.html"
                  target="_blank"
                  rel="noreferrer"
                >
                  Natural History Museum
                </a>
                .
              </p>
            </div>
          </div>

          <div className="card wildlife-card">
            <div className="card-body">
              <h5 className="card-title">Bees</h5>
              <p className="card-text">
                Bees are essential pollinators for flowers, fruits and
                vegetables. Chinbrook Meadows Allotments hosts honey bee hives
                in the Orchard. Find out more about planting for bees{" "}
                <a
                  href="https://www.bbka.org.uk/Blogs/planting-for-bees-blog"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </p>

              <div className="text-center mt-3">
                <img
                  src={BeePhoto}
                  alt="Bees"
                  className="img-fluid rounded shadow-sm"
                  style={{ width: "220px" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="mb-5">
        <h3 className="subheading">Wildlife Gallery</h3>
        <div
          id="photoCarousel"
          className="carousel slide"
          data-bs-ride="carousel"
        >
          <div className="carousel-inner">
            {[
              Fox1,
              Fox2,
              Fox3,
              Fox4,
              Fox5,
              Fox6,
              Fox7,
              Toad,
              Slowworm,
              Photo1,
              Photo2,
              Vetchling,
            ].map((img, i) => (
              <div
                className={`carousel-item ${i === 0 ? "active" : ""}`}
                key={i}
              >
                <img
                  src={img}
                  className="d-block w-50 mx-auto rounded"
                  alt=""
                />
              </div>
            ))}
          </div>

          <button
            className="carousel-control-prev"
            type="button"
            data-bs-target="#photoCarousel"
            data-bs-slide="prev"
          >
            &#8249;
          </button>
          <button
            className="carousel-control-next"
            type="button"
            data-bs-target="#photoCarousel"
            data-bs-slide="next"
          >
            &#8250;
          </button>
        </div>
      </section>
    </div>
  );
}

export default Conservation;
